import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { X, Barcode as BarcodeIcon, ArrowsClockwise, Camera as CameraIcon } from "@phosphor-icons/react";

// 全屏相机扫码。仅在移动端挂载。
// iter 270 (2026-06) 提速:
//   - Android Chrome 等支持原生 BarcodeDetector 的浏览器走硬件解码;
//     iOS Safari 自动回退 zxing。
//   - zxing 只开 UPC/EAN/Code128 一维码,关 TRY_HARDER,解码间隔 500ms → 80ms。
//   - 相机请求 1280×720 + 连续对焦。
//   - onScan 走 ref,父组件重渲染不再重启相机。
const NATIVE_FORMATS = ["upc_a", "upc_e", "ean_13", "ean_8", "code_128"];
const ZXING_FORMATS = [
  BarcodeFormat.UPC_A, BarcodeFormat.UPC_E,
  BarcodeFormat.EAN_13, BarcodeFormat.EAN_8,
  BarcodeFormat.CODE_128,
];

async function nativeFormats() {
  if (typeof window === "undefined" || !("BarcodeDetector" in window)) return null;
  try {
    const supported = await window.BarcodeDetector.getSupportedFormats();
    const formats = NATIVE_FORMATS.filter((f) => supported.includes(f));
    return formats.includes("ean_13") || formats.includes("upc_a") ? formats : null;
  } catch {
    return null;
  }
}

function applyFocus(video) {
  try {
    const track = video?.srcObject?.getVideoTracks?.()[0];
    const caps = track?.getCapabilities?.() || {};
    if (track && Array.isArray(caps.focusMode) && caps.focusMode.includes("continuous")) {
      track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }).catch(() => {});
    }
  } catch {}
}

// iter 271 (2026-06): 相机保持 —— 单扫关闭浮层后不立刻释放 MediaStream,
// 保留 CAMERA_KEEP_MS 供下一次点击秒开;超时 / 页面隐藏 / 离开目录页才真正关闭。
const CAMERA_KEEP_MS = 60000;
const camera = { stream: null, key: "", idleTimer: null };

function stopCameraNow() {
  clearTimeout(camera.idleTimer);
  camera.idleTimer = null;
  try { camera.stream?.getTracks().forEach((t) => t.stop()); } catch {}
  camera.stream = null;
  camera.key = "";
}

async function acquireCamera(constraints) {
  const key = JSON.stringify(constraints);
  clearTimeout(camera.idleTimer);
  camera.idleTimer = null;
  const live = camera.stream?.getVideoTracks?.()[0]?.readyState === "live";
  if (live && camera.key === key) return { stream: camera.stream, reused: true };
  stopCameraNow();
  const stream = await navigator.mediaDevices.getUserMedia(constraints);
  camera.stream = stream;
  camera.key = key;
  return { stream, reused: false };
}

function releaseCamera() {
  clearTimeout(camera.idleTimer);
  camera.idleTimer = setTimeout(stopCameraNow, CAMERA_KEEP_MS);
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden" && camera.idleTimer) stopCameraNow();
  });
}

async function attachStream(stream, video) {
  video.srcObject = stream;
  await video.play().catch(() => {});
  if (video.readyState < 2) {
    await new Promise((resolve) => {
      const done = () => { video.removeEventListener("loadeddata", done); resolve(); };
      video.addEventListener("loadeddata", done);
      setTimeout(done, 1500);
    });
  }
}

function startNative(video, formats, onCode) {
  const detector = new window.BarcodeDetector({ formats });
  let running = true;
  let busy = false;
  const timer = setInterval(async () => {
    if (!running || busy || video.readyState < 2) return;
    busy = true;
    try {
      const codes = await detector.detect(video);
      if (running && codes.length) onCode(codes[0].rawValue);
    } catch {} finally {
      busy = false;
    }
  }, 100);
  return { stop() { running = false; clearInterval(timer); } };
}

function startZxing(video, onCode) {
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, ZXING_FORMATS);
  const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 80, delayBetweenScanSuccess: 300 });
  // reader.scan 只跑解码循环,不接管/释放 stream(decodeFromStream 会在 stop 时关相机)。
  return reader.scan(video, (result) => {
    if (result) onCode(result.getText());
  });
}

export default function BarcodeScannerSheet({ open, onClose, onScan, continuous = false, statusText = "" }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(null);
  const [engine, setEngine] = useState("");
  const [error, setError] = useState("");
  const [hint, setHint] = useState("");
  const [manualCode, setManualCode] = useState("");
  const [showManual, setShowManual] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setError("");
    setHint(continuous ? "连扫模式:扫码后自动加入报价,再扫下一个…" : "对准条码,等待自动识别…");

    let lastCode = "";
    let lastAt = 0;
    const onCode = (raw) => {
      if (!alive) return;
      const code = String(raw || "").trim();
      if (!code) return;
      const now = Date.now();
      if (continuous) {
        if (code === lastCode && now - lastAt < 1500) return;
        lastCode = code;
        lastAt = now;
        onScanRef.current(code);
      } else {
        try { controlsRef.current?.stop(); } catch {}
        if (navigator.vibrate) try { navigator.vibrate(20); } catch {}
        onScanRef.current(code);
      }
    };

    const video = { width: { ideal: 1280 }, height: { ideal: 720 } };
    const constraints = deviceId
      ? { video: { ...video, deviceId: { exact: deviceId } }, audio: false }
      : { video: { ...video, facingMode: { ideal: "environment" } }, audio: false };

    (async () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError("此浏览器不支持相机 (需 HTTPS 且现代浏览器)");
        setShowManual(true);
        return;
      }
      try {
        const [formats, { stream, reused }] = await Promise.all([nativeFormats(), acquireCamera(constraints)]);
        if (!alive) { releaseCamera(); return; }
        const video = videoRef.current;
        await attachStream(stream, video);
        if (!alive) { releaseCamera(); return; }
        if (formats) {
          controlsRef.current = startNative(video, formats, onCode);
          setEngine("native");
        } else {
          controlsRef.current = startZxing(video, onCode);
          setEngine("zxing");
        }
        if (reused) setHint((h) => h.replace("等待自动识别…", "相机已就绪,等待识别…"));
        applyFocus(video);
      } catch (e) {
        if (!alive) return;
        const name = e?.name || "";
        if (name === "NotAllowedError" || name === "SecurityError") {
          setError("相机权限被拒绝。请在地址栏解锁 · 或直接用真机 Safari/Chrome 打开(preview iframe 无法调用相机)");
        } else if (name === "NotFoundError" || name === "OverconstrainedError") {
          setError("未检测到摄像头。请用手机浏览器打开本页");
        } else if (typeof location !== "undefined" && location.protocol !== "https:") {
          setError("需要 HTTPS 才能启动相机");
        } else {
          setError("相机启动失败:" + (e?.message || name || e));
        }
        setShowManual(true);
        return;
      }
      try {
        const list = await BrowserMultiFormatReader.listVideoInputDevices();
        if (alive) setDevices(list);
      } catch {}
    })();

    const t = setTimeout(() => alive && setShowManual(true), 15000);

    return () => {
      alive = false;
      clearTimeout(t);
      try { controlsRef.current?.stop(); } catch {}
      controlsRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
      releaseCamera();
    };
  }, [open, deviceId, continuous]);

  // 离开挂载该组件的页面(如切到报价单 tab)时立即关相机,不等 60s。
  useEffect(() => stopCameraNow, []);

  const switchCamera = () => {
    if (devices.length < 2) return;
    const idx = devices.findIndex((d) => d.deviceId === deviceId);
    const next = devices[(idx + 1) % devices.length];
    setDeviceId(next.deviceId);
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const v = manualCode.trim();
    if (!v) return;
    onScanRef.current(v);
    if (continuous) setManualCode("");
  };

  if (!open) return null;

  return (
    <div
      data-testid="barcode-scanner-sheet"
      className="fixed inset-0 z-[9999] bg-black flex flex-col"
    >
      <div className="flex items-center justify-between px-4 h-14 bg-black/60 text-white z-10">
        <button
          data-testid="barcode-scanner-close"
          onClick={onClose}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 active:bg-white/20"
        >
          <X size={20} weight="bold" />
        </button>
        <div className="text-sm font-medium flex items-center gap-1.5">
          <BarcodeIcon size={16} weight="duotone" />
          {continuous ? "连扫模式" : "扫码"}
          {continuous && (
            <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-500/90 text-black text-[10px] font-bold uppercase" data-testid="scanner-continuous-badge">RAPID</span>
          )}
          {engine && (
            <span className="ml-1 px-1.5 py-0.5 rounded bg-white/15 text-white/70 text-[10px] font-mono" data-testid="barcode-scanner-engine">
              {engine === "native" ? "HW" : "ZX"}
            </span>
          )}
        </div>
        <button
          data-testid="barcode-scanner-switch"
          onClick={switchCamera}
          disabled={devices.length < 2}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 active:bg-white/20 disabled:opacity-30"
        >
          <ArrowsClockwise size={18} weight="bold" />
        </button>
      </div>

      <div className="relative flex-1 overflow-hidden">
        <video
          ref={videoRef}
          data-testid="barcode-scanner-video"
          className="absolute inset-0 w-full h-full object-cover"
          playsInline
          muted
          autoPlay
        />

        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className="w-[78%] max-w-[360px] aspect-[3/2] rounded-2xl border-2 border-white/80 relative shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]">
            <div className="absolute left-0 right-0 top-1/2 h-[2px] bg-red-500/80 animate-pulse" />
          </div>
        </div>

        <div className="absolute left-0 right-0 bottom-24 px-6 text-center text-white text-sm">
          {error ? (
            <span className="text-red-400" data-testid="barcode-scanner-error">{error}</span>
          ) : (
            <span data-testid="barcode-scanner-hint">{hint}</span>
          )}
        </div>

        {continuous && statusText && (
          <div
            data-testid="barcode-scanner-status"
            className="absolute left-4 right-4 top-4 px-3 py-2 rounded-xl bg-emerald-500/95 text-white text-sm text-center shadow-lg animate-in fade-in slide-in-from-top-4"
            key={statusText}
          >
            {statusText}
          </div>
        )}
      </div>

      <div className="bg-black/80 text-white p-4 pb-6">
        {!showManual ? (
          <button
            onClick={() => setShowManual(true)}
            data-testid="barcode-scanner-manual-open"
            className="w-full h-11 rounded-xl border border-white/20 text-sm text-white/80 active:bg-white/10"
          >
            没扫到? 手动输入条码
          </button>
        ) : (
          <form onSubmit={handleManualSubmit} className="flex items-center gap-2">
            <input
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="Enter UPC / barcode"
              inputMode="numeric"
              autoFocus
              data-testid="barcode-scanner-manual-input"
              className="flex-1 h-11 px-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-white/50"
            />
            <button
              type="submit"
              data-testid="barcode-scanner-manual-submit"
              className="h-11 px-4 rounded-xl bg-white text-black font-medium active:bg-white/80"
            >
              查询
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export { CameraIcon as ScannerButtonIcon };
